import React, {ReactNode, useContext, useMemo} from 'react';
import {observer} from 'mobx-react';
import {IconButton, SkeletonPlaceholder, SkeletonText} from '@carbon/react';
import {ChevronLeft} from '@carbon/icons-react';
import classNames from '../../../../lib/class-names';
import {NetworkContext} from '../../lib/context';
import {parseCountyValue} from '../../lib/stores/roster-comparison-store';
import type {ResponseRoster, RosterRef} from '../../lib/stores/roster-comparison-store';
import type {ProviderRostersStatus} from '../../lib/stores/roster-store';
import {formatRosterName, rosterKey} from '../../lib/roster-label';
import {ComparisonRow, rosterHasData} from '../../lib/comparison-row';
import {useProviderRosterList} from '../hooks/use-provider-roster-list';
import {useRosterListRetry} from '../hooks/use-roster-list-retry';
import RosterListError from '../roster-list-error';
import {ComparisonSummary, summariseComparison, SummaryRoster} from './summarise';
import SummaryContent, {useSummaryDisclosures} from './summary-content';
import {BlockedSummary, placeName} from './summary-notices';
import {SummaryPhase, useSummaryOpen} from './use-summary-open';
import {useComparisonRows} from './use-comparison-rows';
import './summary-panel.scss';

const HEADING_ID = 'comparison-summary-heading';
const BODY_ID = 'comparison-summary-body';

// Fields this panel reads from the store's provider roster records
type ProviderRoster = {
    rosterId: string;
    revisionId: string;
    rosterName?: string;
    revisionName?: string;
};

type GeographyFilters = {state: string; county: string; zip: string};

// Named as the roster chips in Filters name it, so a roster reads the same in both places
const rosterName = (rosterId: string, roster?: ProviderRoster) =>
    roster?.rosterName
        ? formatRosterName({rosterName: roster.rosterName, revisionId: roster.revisionId, revisionName: roster.revisionName ?? ''})
        : `Roster ${rosterId}`;

/**
 * The rosters as the summary reads them: named, with the primary marked, each keeping its badge
 * number (slot), the number its response fields carry, and whether it has data for the geography
 *
 * @param responseRosters - The rosters the rows have figures for, from the store
 * @param primary - The primary roster, if one is selected
 * @param providerRosters - The roster list, for each roster's name
 * @param allRows - Every response row, not just the Specialty filter's
 */
const toSummaryRosters = (
    responseRosters: ResponseRoster[],
    primary: RosterRef | null,
    providerRosters: ProviderRoster[],
    allRows: ComparisonRow[],
): SummaryRoster[] => {
    const primaryKey = primary && rosterKey(primary.rosterId, primary.revisionId);
    const detailsByKey = new Map(providerRosters.map((r) => [rosterKey(r.rosterId, r.revisionId), r]));

    return responseRosters.map(({rosterId, revisionId, slot, fieldNumber}) => {
        const key = rosterKey(rosterId, revisionId);
        return {
            key,
            slot,
            fieldNumber,
            name: rosterName(rosterId, detailsByKey.get(key)),
            isPrimary: key === primaryKey,
            hasData: rosterHasData(allRows, fieldNumber),
        };
    });
};

// The county's name from its "CountyName-SSACD" value, or the ZIP; the state when neither is set
const geographyLabel = (grain: 'COUNTY' | 'ZIP', {state, county, zip}: GeographyFilters) => {
    const place = grain === 'ZIP' ? (zip && `ZIP ${zip}`) : parseCountyValue(county).countyName;
    return place || state;
};

// Shown in the heading while the panel is collapsed, e.g. "3 rosters, 5 differences, 1 excluded"
const peekText = ({blocker, ranked, splits, excluded}: ComparisonSummary) => {
    if (blocker) return 'Not enough data';
    const differences = `${splits.length} ${splits.length === 1 ? 'difference' : 'differences'}`;
    return `${ranked.length} rosters, ${differences}${excluded.length ? `, ${excluded.length} excluded` : ''}`;
};

const SummaryMessage = ({children, testId}: {children: ReactNode; testId: string}) => (
    <p className='summary-panel__message' data-testid={testId}>{children}</p>
);

// Shaped like the content it stands in for. aria-hidden: a skeleton has nothing to announce
const SummarySkeleton = () => (
    <div className='summary-panel__skeleton' aria-hidden='true' data-testid='summary-panel-loading'>
        <SkeletonText width='85%' />
        <SkeletonPlaceholder className='summary-panel__skeleton-block' />
        <SkeletonPlaceholder className='summary-panel__skeleton-block' />
        <SkeletonText width='55%' />
        <SkeletonText />
        <SkeletonText />
        <SkeletonText />
    </div>
);

// The roster list's state comes first, as nothing can be selected without the list. 'idle'
// means its load is about to start, so it shows as loading. 'blocked' depends on the loaded
// comparison, so the panel sets it once it has summarised
const summaryPhase = (rosterListStatus: ProviderRostersStatus, comparisonReady: boolean): Exclude<SummaryPhase, 'blocked'> => {
    if (rosterListStatus === 'ready') {
        return comparisonReady ? 'ready' : 'input';
    }
    return rosterListStatus === 'idle' ? 'loading' : rosterListStatus;
};

/**
 * Comparison summary - the left panel of the roster comparison
 *
 * 20rem wide, collapsing to a 5.5rem strip that shows only its toggle. Scrolls with the page.
 * Collapsed until the rosters needed for a comparison are first chosen, it then opens by itself
 * once per visit; after that it keeps whatever the user last chose. While the roster list is
 * loading, empty or failed, or the data leaves nothing to compare, it opens to show that.
 */
const ComparisonSummaryPanel = observer(() => {
    const networkStore = useContext(NetworkContext);
    const comparisonStore = networkStore.rosterComparisonStore;
    const {primaryRoster, responseRosters, measure, selectedSpecialties} = comparisonStore;

    // Loads the roster list if nothing has; the store shares one request between callers
    const {status: rosterListStatus, reload: reloadRosters} = useProviderRosterList();
    const {retry: retryRosters, focusRef: headingRef} = useRosterListRetry<HTMLHeadingElement>(
        rosterListStatus,
        reloadRosters,
    );

    const phase = summaryPhase(rosterListStatus, comparisonStore.isReady);
    const disclosures = useSummaryDisclosures();

    // The rows the table beside the panel shows, and the state of the request behind them
    const comparison = useComparisonRows(comparisonStore);
    const {comparisonData} = comparisonStore;
    const rosters = useMemo(
        () => toSummaryRosters(responseRosters, primaryRoster, networkStore.providerRosters || [], comparisonData || []),
        [responseRosters, primaryRoster, networkStore.providerRosters, comparisonData],
    );
    const summary = useMemo(
        () => (phase === 'ready' && comparison.rows?.length
            ? summariseComparison(comparison.rows, rosters, measure)
            : null),
        [phase, comparison.rows, rosters, measure],
    );
    const settled = summary && !comparison.loading && !comparison.failed ? summary : null;

    // A data gap that blocks the summary is a problem to see without hunting for it, so it opens
    // the panel as the roster list's states do
    const {open, toggle} = useSummaryOpen(settled?.blocker ? 'blocked' : phase, comparisonStore);

    const peek = settled ? peekText(settled) : '';
    // The scope line names the specialty when the table is narrowed to exactly one
    const specialty = selectedSpecialties.length === 1 ? selectedSpecialties[0] : '';
    const geography = geographyLabel(comparisonStore.geographyGrain, comparisonStore.comparisonFilters);
    const noun = comparisonStore.geographyGrain === 'ZIP' ? 'ZIP code' : 'county';

    // One state at a time, in dependency order: the roster list, the requirements, then the
    // comparison request
    const renderBody = (): ReactNode => {
        switch (phase) {
        case 'error':
            // Not announced: Filters already announces the roster list's failure
            return (
                <RosterListError
                    body='There is nothing to summarise until the roster list loads.'
                    onRetry={retryRosters}
                    announce={false}
                    data-testid='summary-panel-error'
                />
            );
        case 'empty':
            return (
                <SummaryMessage testId='summary-panel-no-rosters'>
                    No rosters have been uploaded for this client yet. Add one in Roster, then come back to compare.
                </SummaryMessage>
            );
        case 'loading':
            return <SummarySkeleton />;
        case 'input':
            // Shown when the panel is opened before enough is selected, or after the requirements
            // are lost, since the panel then stays as the user left it.
            // TODO: say "a ZIP code" in ZIP grain, whose requirement is a ZIP, once the backend
            // returns ZIP values; until then the ZIP filter offers none, so only a county can be chosen
            return (
                <SummaryMessage testId='summary-panel-requirements'>
                    The summary appears once a primary roster, at least two rosters and a county are selected.
                </SummaryMessage>
            );
        }

        // The requirements are met: show the comparison request's state
        if (comparison.failed) {
            return (
                <SummaryMessage testId='summary-panel-load-failed'>
                    The comparison didn’t load, so there is nothing to summarise.
                </SummaryMessage>
            );
        }
        // No rows and no failure: a request is in flight or about to start
        if (comparison.loading || !comparison.rows) {
            return <SummarySkeleton />;
        }
        if (!summary) {
            return (
                <SummaryMessage testId='summary-panel-no-specialties'>
                    There are no specialties to summarise for this selection.
                </SummaryMessage>
            );
        }
        if (summary.blocker) {
            return (
                <BlockedSummary
                    blocker={summary.blocker}
                    summary={summary}
                    place={placeName(geography, noun)}
                    noun={noun}
                />
            );
        }
        return (
            <SummaryContent
                summary={summary}
                measure={measure}
                specialty={specialty}
                geography={geography}
                noun={noun}
                disclosures={disclosures}
            />
        );
    };

    return (
        <aside
            className={classNames('summary-panel', {'summary-panel--collapsed': !open})}
            aria-labelledby={HEADING_ID}
            data-testid='comparison-summary-panel'
        >
            {/* The toggle comes first: the panel's left edge is the one that stays put, so the
                control never moves when the column narrows */}
            <div className='summary-panel__head'>
                {/* autoAlign positions the tooltip as fixed and flips it to stay on screen */}
                <IconButton
                    kind='ghost'
                    size='md'
                    autoAlign
                    label={open ? 'Collapse summary' : 'Expand summary'}
                    aria-expanded={open}
                    aria-controls={BODY_ID}
                    onClick={toggle}
                    className='summary-panel__toggle'
                >
                    {/* Points the way the panel will move; turned by the stylesheet when collapsed */}
                    <ChevronLeft size={24} className='summary-panel__toggle-icon' />
                </IconButton>
                {/* Visually hidden while collapsed. The peek sits inside the heading, so the
                    collapsed strip is named "Summary 3 rosters, 5 differences", not "Summary" alone.
                    tabIndex -1: focusable only from script, as it takes focus on retry */}
                <h2 id={HEADING_ID} ref={headingRef} tabIndex={-1} className='summary-panel__title'>
                    Summary{' '}
                    {!open && peek && (
                        <span className='summary-panel__peek' data-testid='summary-peek'>{peek}</span>
                    )}
                </h2>
            </div>
            <div id={BODY_ID} className='summary-panel__body' hidden={!open}>
                {open && renderBody()}
            </div>
        </aside>
    );
});

export default ComparisonSummaryPanel;
